import { useEffect, useRef, useState } from 'react';
import { Rnd } from 'react-rnd';
import { useTransition, animated } from '@react-spring/web';
import { createPortal } from 'react-dom';
import X from '@mui/icons-material/Clear';
import Remove from '@mui/icons-material/Remove';

const DEFAULT_TRANSITION_CONFIG = {
  from: { y: '100%', opacity: 0 },
  enter: { y: '0%', opacity: 1 },
  leave: { y: '120%', opacity: 0 },
  config: { tension: 190, mass: 1.8 },
}

const BasicModal = ({
  open,
  onClose,
  children,
  width = 300,
  height = 800,
  minWidth = 300,
  minHeight = 500,
  dragHandleClassName = 'drag-handle',
  footer = null,
  onMinimize = null,
  autoFullscreen = false,
  showFullscreenButton = false,
  size = "default"
}) => {
  const rndRef = useRef(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const autoAppliedRef = useRef(false)
  const openRef = useRef(false)
  const autoFullscreenOnOpenRef = useRef(false)

  const getViewport = () => ({
    width: window.innerWidth,
    height: window.innerHeight,
  })

  const [viewport, setViewport] = useState(getViewport())

  const clampDimension = (value, min, viewportValue) => {
    const margin = 32
    const maxAllowed = Math.max(min, viewportValue - margin)
    return Math.min(Math.max(value, min), maxAllowed)
  }

  const getSafeMin = (min, viewportValue) =>
    Math.min(min, Math.max(viewportValue - 32, 0))

  const safeMinWidth = getSafeMin(minWidth, viewport.width)
  const safeMinHeight = getSafeMin(minHeight, viewport.height)

  const transition = useTransition(open, DEFAULT_TRANSITION_CONFIG)
// Detectar pantalla grande

  // Configuración por tamaño (solo afecta pantallas grandes)
  const sizeMap = {
    small: { w: 0.01, h: 0.01 },
    compact: { w: 0.5, h: 0.6 },
    default: { w: 0.7, h: 0.8 },
    large: { w: 0.85, h: 0.9 },
  }

  // Si el size no existe, usa default
  const sizeConfig = sizeMap[size] ?? sizeMap.default

  // Tamaño final
  const finalWidth = width ?? viewport.width * sizeConfig.w
  const finalHeight = height ?? viewport.height * sizeConfig.h

  const baseWidth = clampDimension(finalWidth, safeMinWidth, viewport.width)
  const baseHeight = clampDimension(finalHeight, safeMinHeight, viewport.height)

  useEffect(() => {
    if (open && !openRef.current) {
      const vw = typeof window !== 'undefined' ? window.innerWidth : 0
      autoFullscreenOnOpenRef.current = Boolean(autoFullscreen || vw <= 640)
    }
    if (!open) {
      autoFullscreenOnOpenRef.current = false
    }
    openRef.current = open
  }, [open, autoFullscreen])

  const handleToggleFullscreen = () => {
    if (!rndRef.current) return

    const { width: vw, height: vh } = getViewport()
    const safeMinWidthLocal = getSafeMin(minWidth, vw)
    const safeMinHeightLocal = getSafeMin(minHeight, vh)

    if (!isFullscreen) {
      rndRef.current.updatePosition({ x: 0, y: 0 })
      rndRef.current.updateSize({ width: vw, height: vh })
    } else {
      const safeWidth = clampDimension(finalWidth, safeMinWidthLocal, vw)
      const safeHeight = clampDimension(finalHeight, safeMinHeightLocal, vh)
      rndRef.current.updateSize({ width: safeWidth, height: safeHeight })
      rndRef.current.updatePosition({
        x: Math.max((vw - safeWidth) / 2, 0),
        y: Math.max((vh - safeHeight) / 2, 0),
      })
    }

    setIsFullscreen((v) => !v)
  }

  useEffect(() => {
    const handleResize = () => {
      if (!rndRef.current) return

      const { width: vw, height: vh } = getViewport()
      setViewport({ width: vw, height: vh })

      const safeMinWidthLocal = getSafeMin(minWidth, vw)
      const safeMinHeightLocal = getSafeMin(minHeight, vh)

      if (isFullscreen) {
        rndRef.current.updatePosition({ x: 0, y: 0 })
        rndRef.current.updateSize({
          width: vw,
          height: vh,
        })
        return
      }

      const safeWidth = clampDimension(finalWidth, safeMinWidthLocal, vw)
      const safeHeight = clampDimension(finalHeight, safeMinHeightLocal, vh)

      rndRef.current.updateSize({
        width: safeWidth,
        height: safeHeight,
      })

      rndRef.current.updatePosition({
        x: Math.max((vw - safeWidth) / 2, 0),
        y: Math.max((vh - safeHeight) / 2, 0),
      })
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isFullscreen, finalWidth, finalHeight])

  useEffect(() => {
    if (!open) {
      autoAppliedRef.current = false
      return
    }
    if (!autoFullscreenOnOpenRef.current || autoAppliedRef.current || !rndRef.current) return

    const { width: vw, height: vh } = getViewport()
    rndRef.current.updatePosition({ x: 0, y: 0 })
    rndRef.current.updateSize({ width: vw, height: vh })
    setIsFullscreen(true)
    autoAppliedRef.current = true
  }, [open])

  const shouldFullscreen = Boolean(
    autoFullscreenOnOpenRef.current && viewport.width && viewport.height
  )

  const modalRoot =
    typeof document !== 'undefined' ? document.getElementById('modal-root') ?? document.body : null

  if (!modalRoot) return null

  return createPortal(
    transition(
      (style, show) =>
        show && (
        <div className="fixed left-0 top-0 z-[1300] m-0 h-full w-full overflow-hidden bg-black/50 p-0 backdrop-blur-sm">
          <Rnd
            ref={rndRef}
            bounds="window"
            minWidth={safeMinWidth}
            minHeight={safeMinHeight}
            dragHandleClassName={dragHandleClassName}
            cancel=".modal-control"
            enableResizing={!isFullscreen}
            default={{
              x: shouldFullscreen ? 0 : Math.max((viewport.width - baseWidth) / 2, 0),
              y: shouldFullscreen ? 0 : Math.max((viewport.height - baseHeight) / 2, 0),
              width: shouldFullscreen ? viewport.width : baseWidth,
              height: shouldFullscreen ? viewport.height : baseHeight,
            }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              background: '#f2f2f2',
              borderRadius: isFullscreen || shouldFullscreen ? '0' : '1rem',
              overflow: 'hidden',
              maxWidth: '100vw',
              maxHeight: '100vh',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <animated.div
              style={{
                transform: style.y.to((y) => `translateY(${y})`),
                opacity: style.opacity,
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
                <div className="drag-handle bg-primary px-4 py-2 flex items-center justify-end cursor-move">
                <div className="flex items-center gap-1">
                  {onMinimize && (
                    <button
                      type="button"
                      onClick={onMinimize}
                      className="modal-control text-white hover:bg-white/20 rounded p-0.5 transition-colors"
                    >
                      <Remove style={{ fontSize: 18 }} />
                    </button>
                  )}
                  {showFullscreenButton && (
                    <button
                      type="button"
                      onClick={handleToggleFullscreen}
                      className="modal-control text-white hover:bg-white/20 rounded p-0.5 transition-colors"
                    >
                      <span className="block h-[15px] w-[15px] border border-current" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={onClose}
                    className="modal-control text-white hover:bg-white/20 rounded p-0.5 transition-colors"
                  >
                    <X style={{ fontSize: 18 }} />
                  </button>
                </div>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto p-4">
                {children}
              </div>

              {footer && (
                <div className="px-4 py-3 flex justify-end gap-2">
                  {footer}
                </div>
              )}
            </animated.div>
          </Rnd>
        </div>
      )
    ),
    modalRoot
  )
}

export default BasicModal;
